import { useExploreData } from "src/queries/hooks/knowledgeExplore";
import { ENTITY_OPTIONS_PAGE_SIZE } from "../ExploreTab.consts";
import { getColumnFilterType } from "../ExploreTab.utils";
import { NUMERIC_MAP_KEYS } from "../knowledgeFilterMap";
import { toOption, useEntityRefFields } from "./useEntityRefFields.hook";
import useEntityLabels from "./useEntityLabels.hook";

const useEntityOptions = ({ entityType, selectedId, search, pkFieldName, labelFieldName, version, defaultLanguage, enabled = true }) => {
  const refFields = useEntityRefFields({ entityType, pkFieldName, labelFieldName, version });
  const { entityVersion, pkField, labelField, selectedFields } = refFields;
  const filters = search ? [{ field: labelField?.name ?? pkField?.name, op: "CONTAINS", value: search }] : [];

  const dataQuery = useExploreData({
    schemaType: entityType,
    version: entityVersion,
    fields: selectedFields,
    filters,
    pagination: { offset: 0, limit: ENTITY_OPTIONS_PAGE_SIZE },
    enabled
  });

  const result = dataQuery.data?.[entityType] ?? null;
  const pageOptions = (result?.items ?? []).map((item) => toOption(item, pkField, labelField, defaultLanguage));

  const cachedLabels = useEntityLabels({ entityType, pkFieldName, labelFieldName, version, defaultLanguage, enabled: false });
  const cachedSelectedOption = selectedId
    ? cachedLabels.options.find((option) => option.key === selectedId) ?? null
    : null;

  // The current selection may live beyond the first page (and not match the search), so
  // resolve its label with a targeted by-id lookup — otherwise the field shows a bare id.
  // Numeric pks use EQ, string pks use IS (exact ilike); see knowledge filtering ops.
  const pageHasSelected = Boolean(result) && pageOptions.some((option) => option.key === selectedId);
  const resolveSelected =
    Boolean(selectedId) && Boolean(pkField) && Boolean(result) && !pageHasSelected && !cachedSelectedOption;
  const pkOp = NUMERIC_MAP_KEYS.includes(getColumnFilterType(pkField)?.mapKey) ? "EQ" : "IS";
  const selectedQuery = useExploreData({
    schemaType: entityType,
    version: entityVersion,
    fields: resolveSelected ? selectedFields : [],
    filters: resolveSelected ? [{ field: pkField.name, op: pkOp, value: selectedId }] : [],
    pagination: { offset: 0, limit: 1 },
    enabled
  });

  const selectedRow = selectedQuery.data?.[entityType]?.items?.[0] ?? null;
  const selectedOption =
    cachedSelectedOption ?? (selectedRow ? toOption(selectedRow, pkField, labelField, defaultLanguage) : null);
  const options = selectedOption && !pageOptions.some((o) => o.key === selectedOption.key)
    ? [selectedOption, ...pageOptions]
    : pageOptions;

  return {
    options,
    totalCount: result?.total_count ?? 0,
    isLoading: refFields.isLoading || dataQuery.isLoading || selectedQuery.isLoading,
    labelFieldName: labelField?.name ?? null,
    pkFieldName: pkField?.name ?? null
  };
};

export default useEntityOptions;
